'use client';

import {
  Badge,
  BodyDefault,
  IconButton,
  MetaDefault,
  Spinner,
  Tooltip,
} from '@terra/ui-design-system';
import { HStack } from '@terra/ui-styled-system';
import { Clock01Icon, PauseIcon, PlayIcon, UserIcon } from '@terra/ui-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  isAbortUiLocked,
  isActiveSession,
  type CrawlerSession,
  type CrawlerSessionType,
} from '../../hooks/useCrawlerSessions';
import {
  formatElapsedTimer,
  formatRelativeCrawlTime,
  isAutomatedCrawlerType,
  oldestCreatedAt,
  type RelativeCrawlTime,
} from './crawlerHistory.utils';

const EMPTY_SET = new Set<string>();
const WAITING_TIMEOUT_MS = 30000;

type CrawlerCredentialRowProps = {
  credentialId: string | null;
  credentialName: string;
  credentialRole?: string;
  isAnonymous?: boolean;
  isDisabled?: boolean;
  sessions: CrawlerSession[];
  lastFinishedAt?: string;
  lastCrawlerType?: CrawlerSessionType;
  nowMs: number;
  hideDivider?: boolean;
  onTriggerCrawler: () => void;
  onAbortByCredential?: (credentialId: string | null) => void;
  isTriggering?: boolean;
  abortingCredentialIds?: Set<string>;
  isAbortingAll?: boolean;
};

function relativeCrawlTimeLabel({
  relative,
  neverLabel,
  justNowLabel,
  minutesAgo,
  hoursAgo,
  daysAgo,
}: {
  relative: RelativeCrawlTime | null;
  neverLabel: string;
  justNowLabel: string;
  minutesAgo: (count: number) => string;
  hoursAgo: (count: number) => string;
  daysAgo: (count: number) => string;
}): string {
  if (!relative) {
    return neverLabel;
  }

  if (relative.kind === 'justNow') {
    return justNowLabel;
  }

  if (relative.kind === 'minutes') {
    return minutesAgo(relative.count);
  }

  if (relative.kind === 'hours') {
    return hoursAgo(relative.count);
  }

  if (relative.kind === 'days') {
    return daysAgo(relative.count);
  }

  return relative.label;
}

export function CrawlerCredentialRow({
  credentialId,
  credentialName,
  credentialRole,
  isAnonymous,
  isDisabled,
  sessions,
  lastFinishedAt,
  lastCrawlerType,
  nowMs,
  hideDivider,
  onTriggerCrawler,
  onAbortByCredential,
  isTriggering,
  abortingCredentialIds = EMPTY_SET,
  isAbortingAll = false,
}: CrawlerCredentialRowProps) {
  const t = useTranslations('crawler.crawlerMonitorPanel');
  const [isWaitingForSessions, setIsWaitingForSessions] = useState(false);
  const [abortingSinceMs, setAbortingSinceMs] = useState<number | null>(null);
  const wasTriggering = useRef(false);
  const waitingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSessions = sessions.filter(isActiveSession);
  const activeAutomatedSessions = activeSessions.filter((session) =>
    isAutomatedCrawlerType(session.crawlerType),
  );
  const hasActiveAutomated = activeAutomatedSessions.length > 0;
  const hasManualActive = activeSessions.some(
    (session) => session.crawlerType === 'MANUAL',
  );
  const clearWaitingTimeout = () => {
    if (waitingTimeoutRef.current) {
      clearTimeout(waitingTimeoutRef.current);
      waitingTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearWaitingTimeout();
    };
  }, []);
  useEffect(() => {
    const triggerJustStarted = !!isTriggering && !wasTriggering.current;
    const triggerJustEnded =
      !isTriggering && wasTriggering.current && isWaitingForSessions;

    if (triggerJustStarted) {
      setIsWaitingForSessions(true);
      clearWaitingTimeout();
      waitingTimeoutRef.current = setTimeout(
        () => setIsWaitingForSessions(false),
        WAITING_TIMEOUT_MS,
      );
    }

    wasTriggering.current = !!isTriggering;
    if (hasActiveAutomated && isWaitingForSessions) {
      setIsWaitingForSessions(false);
      clearWaitingTimeout();
    }

    if (triggerJustEnded && !hasActiveAutomated) {
      setIsWaitingForSessions(false);
      clearWaitingTimeout();
    }
  }, [isTriggering, hasActiveAutomated, isWaitingForSessions]);
  const showStartLoading = !!isTriggering || isWaitingForSessions;
  const credentialTrackingId = credentialId || 'anonymous';
  const allActiveAborting =
    activeAutomatedSessions.length > 0 &&
    activeAutomatedSessions.every((session) => session.status === 'ABORTING');
  const isLocallyAborting =
    isAbortingAll || abortingCredentialIds.has(credentialTrackingId);
  const abortingActive = isLocallyAborting || allActiveAborting;

  useEffect(() => {
    if (abortingActive) {
      setAbortingSinceMs((prev) => prev ?? nowMs);
    } else {
      setAbortingSinceMs(null);
    }
  }, [abortingActive, nowMs]);

  const isAbortingCredential = isAbortUiLocked({
    isLocallyAborting,
    allActiveAborting,
    abortingSinceMs,
    nowMs,
  });
  const oldestActiveStartedAt = oldestCreatedAt(
    activeAutomatedSessions.map((session) => session.createdAt),
  );
  const relative = formatRelativeCrawlTime({
    date: lastFinishedAt,
    now: new Date(nowMs),
  });
  const isJustNow = relative?.kind === 'justNow';
  const showTrackingIcon =
    hasManualActive || isJustNow || lastCrawlerType === 'MANUAL';
  const relativeLabel = relativeCrawlTimeLabel({
    relative,
    neverLabel: t('neverCrawled'),
    justNowLabel: t('justNow'),
    minutesAgo: (count) => t('minutesAgo', { count }),
    hoursAgo: (count) => t('hoursAgo', { count }),
    daysAgo: (count) => t('daysAgo', { count }),
  });
  const runLabel = t('runCredentialAriaLabel');
  const stopLabel = t('stopCredentialAriaLabel');

  return (
    <HStack
      w="full"
      justifyContent="space-between"
      alignItems="center"
      px="3"
      py="2"
      gap="2"
      borderBottomWidth={hideDivider ? undefined : '1px'}
      borderColor={hideDivider ? undefined : 'border.muted'}
    >
      <HStack gap="2" alignItems="center" flex="1" minW="0">
        <BodyDefault truncate flexShrink={1} minW="0">
          {credentialName}
        </BodyDefault>
        {isAnonymous ? (
          <MetaDefault color="fg.subtle" flexShrink={0}>
            {t('noAuth')}
          </MetaDefault>
        ) : credentialRole ? (
          <Badge variant="outline" size="sm" flexShrink={0}>
            {credentialRole}
          </Badge>
        ) : null}
      </HStack>

      <HStack gap="2" alignItems="center" flexShrink={0}>
        {hasActiveAutomated && oldestActiveStartedAt ? (
          <HStack gap="1" alignItems="center">
            <MetaDefault color="fg.accent.default">{t('crawling')}</MetaDefault>
            <MetaDefault color="fg.accent.default">
              {formatElapsedTimer({
                startedAt: oldestActiveStartedAt,
                now: new Date(nowMs),
              })}
            </MetaDefault>
          </HStack>
        ) : (
          <HStack gap="1.5" alignItems="center" color="fg.muted">
            {showTrackingIcon ? (
              <UserIcon size={14} />
            ) : (
              <Clock01Icon size={16} />
            )}
            <MetaDefault color="fg.muted">{relativeLabel}</MetaDefault>
          </HStack>
        )}

        {hasActiveAutomated ? (
          <Tooltip title={stopLabel} positioning={{ strategy: 'fixed' }}>
            <IconButton
              variant="ghost"
              size="xs"
              disabled={isAbortingCredential}
              onClick={() => onAbortByCredential?.(credentialId)}
              aria-label={stopLabel}
            >
              {isAbortingCredential ? (
                <Spinner size="xs" />
              ) : (
                <PauseIcon size={16} />
              )}
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={runLabel} positioning={{ strategy: 'fixed' }}>
            <IconButton
              variant="ghost"
              size="xs"
              disabled={!!isDisabled || showStartLoading}
              onClick={onTriggerCrawler}
              aria-label={runLabel}
            >
              {showStartLoading ? (
                <Spinner size="xs" />
              ) : (
                <PlayIcon size={16} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    </HStack>
  );
}
